import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  BookOpen, Plus, Upload, Download, Loader2, Sparkles, ChevronUp, ChevronDown,
  Trash2, History, RotateCcw, Check, X as XIcon,
} from 'lucide-react';

const YEAR = 2027;
const STATUS_META = {
  needs_review: { label: 'Needs review', cls: 'bg-amber-100 text-amber-800', dot: '#D97706' },
  updated:      { label: 'Updated for 2027', cls: 'bg-blue-100 text-blue-800', dot: '#2563EB' },
  final:        { label: 'Final', cls: 'bg-emerald-100 text-emerald-800', dot: '#059669' },
};
const AI_QUICK = [
  { label: 'Modernize for 2027', text: 'Update this policy for 2027 — modern language, current best practices, keep the same intent.' },
  { label: 'Plain language', text: 'Rewrite this policy in plain, friendly language a new hire can easily understand, without losing meaning.' },
  { label: 'Tighten', text: 'Make this policy more concise. Remove redundancy while keeping every requirement.' },
];

export default function HandbookPanel({ api }) {
  const [sections, setSections] = useState([]);
  const [summary, setSummary] = useState({ total: 0, final: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);       // full section incl. versions
  const [form, setForm] = useState({ title: '', body: '', status: 'needs_review' });
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiDraft, setAiDraft] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async (keepSelection = true) => {
    try {
      const { data } = await api.get(`/handbook/sections?year=${YEAR}`);
      setSections(data.sections);
      setSummary(data.summary);
      if (!keepSelection || !data.sections.some(s => s.id === selectedId)) {
        setSelectedId(data.sections[0]?.id || null);
      }
    } catch { toast.error('Failed to load handbook'); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, selectedId]);
  useEffect(() => { load(false); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    setShowHistory(false); setAiDraft('');
    api.get(`/handbook/sections/${selectedId}`).then(({ data }) => {
      setDetail(data);
      setForm({ title: data.title, body: data.body, status: data.status });
    }).catch(() => {});
  }, [selectedId, api]);

  const dirty = detail && (form.title !== detail.title || form.body !== detail.body || form.status !== detail.status);

  const save = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/handbook/sections/${detail.id}`, form);
      setDetail(data);
      toast.success('Section saved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const addSection = async () => {
    const title = window.prompt('New section title:');
    if (!title?.trim()) return;
    try {
      const { data } = await api.post('/handbook/sections', { year: YEAR, title });
      await load();
      setSelectedId(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const removeSection = async () => {
    if (!detail || !window.confirm(`Delete "${detail.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/handbook/sections/${detail.id}`);
      toast.success('Section deleted');
      setSelectedId(null);
      load(false);
    } catch { toast.error('Delete failed'); }
  };

  const move = async (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= sections.length) return;
    const next = [...sections];
    [next[idx], next[to]] = [next[to], next[idx]];
    setSections(next);
    try { await api.post('/handbook/reorder', { year: YEAR, ids: next.map(s => s.id) }); }
    catch { toast.error('Reorder failed'); load(); }
  };

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const replace = sections.length > 0
      ? window.confirm(`Replace the existing ${sections.length} sections with a fresh import?`)
      : false;
    if (sections.length > 0 && !replace) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('year', String(YEAR));
      fd.append('replace', replace ? 'true' : 'false');
      const { data } = await api.post('/handbook/import', fd);
      toast.success(`Imported ${data.imported} sections from the PDF`);
      load(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    setImporting(false);
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const resp = await api.get(`/handbook/export?year=${YEAR}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `TNS_Employee_Handbook_${YEAR}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed — add at least one section first'); }
    setExporting(false);
  };

  const runAI = async (instruction) => {
    const text = (instruction || aiPrompt).trim();
    if (!text) { toast.error('Tell the assistant what to do first'); return; }
    setAiBusy(true); setAiDraft('');
    try {
      const { data } = await api.post('/handbook/ai', { instruction: text, title: form.title, body: form.body });
      setAiDraft(data.draft);
    } catch (e) { toast.error(e.response?.data?.detail || 'AI assist failed'); }
    setAiBusy(false);
  };

  const restore = async (idx) => {
    if (!window.confirm('Restore this earlier version? Your current text is snapshotted first.')) return;
    try {
      const { data } = await api.post(`/handbook/sections/${detail.id}/restore`, { version_index: idx });
      setDetail(data);
      setForm({ title: data.title, body: data.body, status: data.status });
      toast.success('Version restored');
      load();
    } catch { toast.error('Restore failed'); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#6B3FA0]" /></div>;

  const pct = summary.total ? Math.round((summary.final / summary.total) * 100) : 0;

  return (
    <div data-testid="handbook-panel">
      {/* Header */}
      <div className="ceo-card-elevated p-4 mb-4 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-[#6B3FA0]" />
          <div>
            <div className="font-bold text-[#2D1B4E]">{YEAR} Employee Handbook</div>
            <div className="text-[11px] text-gray-500">{summary.final}/{summary.total} sections final · {pct}% ready</div>
          </div>
        </div>
        <div className="h-2 flex-1 min-w-[120px] bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} data-testid="handbook-progress" />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => fileRef.current?.click()} disabled={importing}
                  className="px-3 py-1.5 text-xs rounded-lg border hover:border-[#6B3FA0] hover:text-[#6B3FA0] inline-flex items-center gap-1.5 bg-white"
                  data-testid="handbook-import-btn">
            {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Import PDF
          </button>
          <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={onImport} data-testid="handbook-import-input" />
          <button onClick={onExport} disabled={exporting || !sections.length}
                  className="px-3 py-1.5 text-xs rounded-lg bg-[#6B3FA0] text-white hover:bg-[#5A2F8E] inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="handbook-export-btn">
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Export {YEAR} PDF
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {/* Section list */}
        <div className="ceo-card-elevated p-3 md:max-h-[70vh] overflow-y-auto" data-testid="handbook-section-list">
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Sections ({sections.length})</span>
            <button onClick={addSection} className="text-[#6B3FA0] hover:underline text-xs inline-flex items-center gap-0.5" data-testid="handbook-add-section">
              <Plus className="h-3 w-3" /> Add
            </button>
          </div>
          {sections.length === 0 && (
            <p className="text-xs text-gray-400 italic p-2">Nothing yet — import your current handbook PDF to get a head start, or add a section.</p>
          )}
          {sections.map((s, i) => (
            <div key={s.id}
                 className={`group flex items-center gap-1.5 px-2 py-1.5 rounded-lg cursor-pointer text-xs mb-0.5 ${selectedId === s.id ? 'bg-[#EDE7F6] text-[#2D1B4E] font-semibold' : 'hover:bg-gray-50 text-gray-700'}`}
                 onClick={() => setSelectedId(s.id)}
                 data-testid={`handbook-section-${s.id}`}>
              <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: STATUS_META[s.status]?.dot || '#999' }} />
              <span className="flex-1 truncate">{s.title}</span>
              <span className="opacity-0 group-hover:opacity-100 flex flex-col">
                <button onClick={(e) => { e.stopPropagation(); move(i, -1); }} className="text-gray-400 hover:text-[#6B3FA0]" data-testid={`handbook-up-${s.id}`}><ChevronUp className="h-3 w-3" /></button>
                <button onClick={(e) => { e.stopPropagation(); move(i, 1); }} className="text-gray-400 hover:text-[#6B3FA0]" data-testid={`handbook-down-${s.id}`}><ChevronDown className="h-3 w-3" /></button>
              </span>
            </div>
          ))}
        </div>

        {/* Editor */}
        <div className="md:col-span-2 space-y-3">
          {!detail ? (
            <div className="ceo-card-elevated p-10 text-center text-sm text-gray-400">Pick a section to edit — or import your current handbook.</div>
          ) : (
            <div className="ceo-card-elevated p-4 space-y-3" data-testid="handbook-editor">
              <div className="flex items-center gap-2 flex-wrap">
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                       className="flex-1 min-w-[220px] font-semibold text-sm border rounded-lg px-3 py-2 bg-white text-gray-900"
                       data-testid="handbook-title-input" />
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                        className="border rounded-lg px-2 py-2 text-xs bg-white text-gray-900" data-testid="handbook-status-select">
                  {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                </select>
                <button onClick={save} disabled={!dirty || saving}
                        className="px-3 py-2 text-xs rounded-lg bg-[#6B3FA0] text-white hover:bg-[#5A2F8E] disabled:opacity-40 inline-flex items-center gap-1"
                        data-testid="handbook-save-btn">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
                </button>
                <button onClick={removeSection} className="text-gray-400 hover:text-red-600 p-1.5" title="Delete section" data-testid="handbook-delete-btn">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                        rows={16} placeholder="Policy text…"
                        className="w-full border rounded-xl px-3 py-2 text-sm leading-relaxed bg-white text-gray-900 focus-visible:outline-none focus-visible:ring-2"
                        data-testid="handbook-body-input" />
              <div className="flex items-center justify-between text-[10px] text-gray-400">
                <span>Last saved {new Date(detail.updated_at).toLocaleString()} by {detail.updated_by}</span>
                <button onClick={() => setShowHistory(h => !h)} className="inline-flex items-center gap-1 hover:text-[#6B3FA0]" data-testid="handbook-history-btn">
                  <History className="h-3 w-3" /> {detail.versions?.length || 0} version{(detail.versions?.length || 0) === 1 ? '' : 's'}
                </button>
              </div>
              {showHistory && (
                <div className="border rounded-lg divide-y max-h-48 overflow-y-auto" data-testid="handbook-history-list">
                  {(detail.versions || []).slice().reverse().map((v, ri) => {
                    const idx = detail.versions.length - 1 - ri;
                    return (
                      <div key={idx} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                        <span className="flex-1 truncate text-gray-600">{v.title} — {new Date(v.at).toLocaleString()} · {v.by}{v.note ? ` · ${v.note}` : ''}</span>
                        <button onClick={() => restore(idx)} className="text-[#6B3FA0] hover:underline inline-flex items-center gap-0.5" data-testid={`handbook-restore-${idx}`}>
                          <RotateCcw className="h-3 w-3" /> Restore
                        </button>
                      </div>
                    );
                  })}
                  {(detail.versions || []).length === 0 && <p className="text-xs text-gray-400 italic p-3">No earlier versions yet.</p>}
                </div>
              )}
            </div>
          )}

          {/* AI assistant */}
          {detail && (
            <div className="ceo-card-elevated p-4 space-y-2" data-testid="handbook-ai-card">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-[#2D1B4E]">
                <Sparkles className="h-4 w-4 text-[#E91E63]" /> AI writing assistant
                <span className="text-[10px] font-normal text-gray-400">— drafts only; your edits are final</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {AI_QUICK.map(q => (
                  <button key={q.label} onClick={() => runAI(q.text)} disabled={aiBusy}
                          className="px-2.5 py-1 text-[11px] rounded-full border hover:border-[#E91E63] hover:text-[#E91E63] bg-white"
                          data-testid={`handbook-ai-quick-${q.label.replace(/\s+/g, '-').toLowerCase()}`}>
                    {q.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
                       onKeyDown={e => e.key === 'Enter' && runAI()}
                       placeholder='e.g. "Draft a social media conduct policy" or "add remote-work language"'
                       className="flex-1 border rounded-lg px-3 py-1.5 text-xs bg-white text-gray-900" data-testid="handbook-ai-input" />
                <button onClick={() => runAI()} disabled={aiBusy}
                        className="px-3 py-1.5 text-xs rounded-lg bg-[#E91E63] text-white hover:bg-[#C2185B] inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid="handbook-ai-run">
                  {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Draft
                </button>
              </div>
              {aiDraft && (
                <div className="border border-[#E91E63]/30 bg-pink-50/50 rounded-xl p-3 space-y-2" data-testid="handbook-ai-draft">
                  <p className="text-xs whitespace-pre-wrap leading-relaxed text-gray-800 max-h-56 overflow-y-auto">{aiDraft}</p>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setAiDraft('')} className="px-2.5 py-1 text-[11px] rounded border bg-white text-gray-500 inline-flex items-center gap-1" data-testid="handbook-ai-discard">
                      <XIcon className="h-3 w-3" /> Discard
                    </button>
                    <button onClick={() => { setForm(f => ({ ...f, body: f.body ? `${f.body}\n\n${aiDraft}` : aiDraft })); setAiDraft(''); toast.success('Appended — review and Save'); }}
                            className="px-2.5 py-1 text-[11px] rounded border bg-white text-[#6B3FA0] border-[#6B3FA0]/40" data-testid="handbook-ai-append">
                      Append to section
                    </button>
                    <button onClick={() => { setForm(f => ({ ...f, body: aiDraft })); setAiDraft(''); toast.success('Replaced — review and Save'); }}
                            className="px-2.5 py-1 text-[11px] rounded bg-[#6B3FA0] text-white" data-testid="handbook-ai-replace">
                      Replace section text
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
